import { Link } from "@tanstack/react-router";
import { ArrowUpRight, CalendarDays, Flag, Trophy } from "lucide-react";
import { outcomeLabel } from "@/features/matches/matchCompetition";
import type { Tournament } from "@/lib/types";

function shortDate(value: string) {
  // Date-only values represent the event's local calendar day, not UTC.
  return new Date(`${value}T12:00:00`).toLocaleDateString("en", {
    day: "numeric",
    month: "short",
  });
}

export function TournamentRow({
  tournament,
  teamSlug,
  seasonId,
  latest = false,
  decider,
}: {
  tournament: Tournament;
  teamSlug: string;
  seasonId?: string;
  latest?: boolean;
  decider?: { score: string; penalties?: string };
}) {
  return (
    <Link
      to="/teams/$teamSlug"
      params={{ teamSlug }}
      search={(prev) => ({
        ...prev,
        season: seasonId ?? prev.season,
        view: "tournaments",
        tournament: tournament.id,
        page: 1,
        q: undefined,
      })}
      className={`scorebook-event scorebook-directory-event${latest ? " scorebook-latest-event" : ""}`}
    >
      <TournamentEventContent tournament={tournament} decider={decider} latest={latest} />
    </Link>
  );
}

export function TournamentEventContent({
  tournament: t,
  decider,
  latest = false,
}: {
  tournament: Pick<Tournament, "name" | "status" | "start_date" | "end_date" | "outcome" | "location" | "division" | "summary" | "match_count">;
  decider?: { score: string; penalties?: string };
  latest?: boolean;
}) {
  const status =
    t.status === "upcoming"
      ? "Upcoming"
      : t.status === "in_progress"
        ? "In progress"
        : "Completed";
  const Icon =
    t.status === "upcoming"
      ? CalendarDays
      : t.status === "completed" && t.outcome === "champions"
        ? Trophy
        : Flag;
  const dates = t.start_date
    ? `${shortDate(t.start_date)}${t.end_date && t.end_date !== t.start_date ? `–${shortDate(t.end_date)}` : ""}`
    : "Dates not announced";
  const result =
    t.status === "upcoming"
      ? "Up next"
      : t.outcome
        ? latest && t.outcome === "champions"
          ? "Winners"
          : outcomeLabel(t.outcome)
        : status;

  return (
    <>
      <div className="scorebook-event-icon" aria-hidden="true">
        <Icon />
      </div>
      <div className="scorebook-event-title">
        <div className="scorebook-event-meta">
          <span className="scorebook-event-status" data-status={t.status}>
            {status}
          </span>
          <span>{dates}</span>
        </div>
        <h3>{t.name}</h3>
        <p>{[t.location, t.division].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="scorebook-event-record">
        <strong>{result}</strong>
        <span>
          {t.summary
            ? t.summary.played
              ? `${t.summary.played} played · ${t.summary.won}W · ${t.summary.drawn}D · ${t.summary.lost}L`
              : "Fixtures ready to view"
            : `${t.match_count ?? 0} matches`}
        </span>
        {decider ? (
          <span className="scorebook-event-decider">
            Final {decider.score}
            {decider.penalties ? <><br />{decider.penalties}</> : null}
          </span>
        ) : null}
      </div>
      <ArrowUpRight size={20} aria-hidden="true" />
    </>
  );
}
